<!-- dashboard/routes/dashboard/settings/account/components/AutoResolveCustom.vue -->
<script setup>
import { computed, onMounted, onActivated } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAutoResolve } from 'dashboard/composables/useAutoResolve';

import SectionLayout from './SectionLayout.vue';
import Switch from 'next/switch/Switch.vue';

import HierarchyIndicator from './auto-resolve/HierarchyIndicator.vue';
import TabNavigation from './auto-resolve/TabNavigation.vue';
import DefaultConfigForm from './auto-resolve/DefaultConfigForm.vue';
import ConfigSection from './auto-resolve/ConfigSection.vue';

const { t } = useI18n();

const {
  isEnabled,
  isSubmitting,
  activeTab,
  defaultConfig,
  labelConfigs,
  agentConfigs,
  teamConfigs,
  labelOptions,
  labelOptionsForConfig,
  agentOptions,
  teamOptions,
  hasSpecificConfigs,
  DURATION_OPTIONS,
  CONFIG_TYPES,
  formatFriendlyDuration,
  loadRequiredData,
  toggleAutoResolve,
  saveDefaultConfig,
  upsertConfig,
  removeConfig,
  getConfigForEdit,
} = useAutoResolve();

// ✅ NOVO: Computed properties para detectar configs existentes
const hasLabelConfigs = computed(() => labelConfigs.value.size > 0);
const hasAgentConfigs = computed(() => agentConfigs.value.size > 0);
const hasTeamConfigs = computed(() => teamConfigs.value.size > 0);

onMounted(() => loadRequiredData());
onActivated(() => loadRequiredData());

const getConfigType = () => {
  if (activeTab.value === 'labels') return CONFIG_TYPES.LABEL;
  if (activeTab.value === 'agents') return CONFIG_TYPES.AGENT;
  return CONFIG_TYPES.TEAM;
};

const handleConfigAdd = async ({ key, config, onSuccess }) => {
  const result = await upsertConfig(getConfigType(), key, config);
  if (result.success && onSuccess) onSuccess();
};

const handleConfigEdit = async ({ key, config, onSuccess }) => {
  const result = await upsertConfig(getConfigType(), key, config);
  if (result.success && onSuccess) onSuccess();
};

const handleConfigRemove = async ({ key }) => {
  await removeConfig(getConfigType(), key);
};
</script>

<template>
  <SectionLayout
    :title="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.TITLE')"
    :description="t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE.NOTE')"
    :hide-content="!isEnabled"
    with-border
  >
    <template #headerActions>
      <Switch v-model="isEnabled" @change="toggleAutoResolve" />
    </template>

    <!-- ✅ MODIFICADO: Passar props para HierarchyIndicator -->
    <HierarchyIndicator
      v-if="hasSpecificConfigs"
      :has-label-configs="hasLabelConfigs"
      :has-agent-configs="hasAgentConfigs"
      :has-team-configs="hasTeamConfigs"
    />

    <TabNavigation
      v-model="activeTab"
      :label-count="labelConfigs.size"
      :agent-count="agentConfigs.size"
      :team-count="teamConfigs.size"
    />

    <DefaultConfigForm
      v-if="activeTab === 'padrão'"
      :config="defaultConfig"
      :label-options="labelOptions"
      :duration-options="DURATION_OPTIONS"
      :is-submitting="isSubmitting"
      @update:config="defaultConfig = $event"
      @submit="saveDefaultConfig"
    />

    <ConfigSection
      v-else-if="activeTab === 'labels'"
      type="label"
      :configs="labelConfigs"
      :select-options="labelOptionsForConfig"
      :label-options="labelOptions"
      :duration-options="DURATION_OPTIONS"
      :is-submitting="isSubmitting"
      :format-duration="formatFriendlyDuration"
      :get-config-for-edit="getConfigForEdit"
      @add="handleConfigAdd"
      @edit="handleConfigEdit"
      @remove="handleConfigRemove"
    />

    <ConfigSection
      v-else-if="activeTab === 'agents'"
      type="agent"
      :configs="agentConfigs"
      :select-options="agentOptions"
      :label-options="labelOptions"
      :duration-options="DURATION_OPTIONS"
      :is-submitting="isSubmitting"
      :format-duration="formatFriendlyDuration"
      :get-config-for-edit="getConfigForEdit"
      @add="handleConfigAdd"
      @edit="handleConfigEdit"
      @remove="handleConfigRemove"
    />

    <ConfigSection
      v-else-if="activeTab === 'teams'"
      type="team"
      :configs="teamConfigs"
      :select-options="teamOptions"
      :label-options="labelOptions"
      :duration-options="DURATION_OPTIONS"
      :is-submitting="isSubmitting"
      :format-duration="formatFriendlyDuration"
      :get-config-for-edit="getConfigForEdit"
      @add="handleConfigAdd"
      @edit="handleConfigEdit"
      @remove="handleConfigRemove"
    />
  </SectionLayout>
</template>